import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, useTenant } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Loader2, ArrowLeft, Plus, Edit, Trash2, MoreVertical, 
  CheckCircle2, Circle, ChevronDown, ChevronRight, Users, 
  MessageSquare, Calendar, Clock, Send, UserPlus, X,
  Repeat, AlertTriangle, Play, Pause, ClipboardList
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { useTranslation } from "../contexts/I18nContext";

export default function ProjectDetailPage() {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const notesEndRef = useRef(null);
  const { user: currentUser } = useTenant();
  
  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSteps, setExpandedSteps] = useState({});

  // Dialogs
  const [stepDialog, setStepDialog] = useState({ open: false, step: null, parentId: null });
  const [participantDialog, setParticipantDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });

  // Forms
  const [stepForm, setStepForm] = useState({ titolo: "", descrizione: "", data_scadenza: "", assegnato_a: "" });
  const [noteInput, setNoteInput] = useState("");
  const [selectedStepForNote, setSelectedStepForNote] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState("");
  const [selectedParticipants, setSelectedParticipants] = useState(new Set());
  const [participantSearch, setParticipantSearch] = useState("");

  const fetchProject = useCallback(async () => {
    try {
      const [projectRes, usersRes] = await Promise.all([
        api.get(`${getApiPrefix()}/projects/${projectId}`),
        api.get(`${getApiPrefix()}/users-list`)
      ]);
      setProject(projectRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadProject"));
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate, t]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  useEffect(() => {
    // Scroll to bottom when notes change
    notesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [project?.notes]);

  const priorityColors = {
    alta: "bg-red-100 text-red-700 border-red-200",
    media: "bg-yellow-100 text-yellow-700 border-yellow-200",
    bassa: "bg-green-100 text-green-700 border-green-200"
  };

  const statusLabels = {
    da_fare: { label: "Da Fare", color: "bg-slate-100 text-slate-700" },
    in_corso: { label: "In Corso", color: "bg-blue-100 text-blue-700" },
    completato: { label: "Completato", color: "bg-green-100 text-green-700" },
    sospeso: { label: "Sospeso", color: "bg-orange-100 text-orange-700" }
  };

  const recurrenceLabels = {
    giornaliero: "Giornaliero",
    settimanale: "Settimanale",
    mensile: "Mensile",
    annuale: "Annuale"
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("it-IT");
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("it-IT", { 
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" 
    });
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  // Build hierarchical steps
  const buildStepTree = (steps) => {
    const map = {};
    const roots = [];

    steps.forEach(step => {
      map[step.id] = { ...step, children: [] };
    });

    steps.forEach(step => {
      if (step.parent_step_id && map[step.parent_step_id]) {
        map[step.parent_step_id].children.push(map[step.id]);
      } else {
        roots.push(map[step.id]);
      }
    });

    return roots;
  };

  const stepTree = project?.steps ? buildStepTree(project.steps) : [];

  const toggleStepExpand = (stepId) => {
    setExpandedSteps(prev => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  // Step CRUD
  const openStepDialog = (step = null, parentId = null) => {
    setStepForm({
      titolo: step?.titolo || "",
      descrizione: step?.descrizione || "",
      data_scadenza: step?.data_scadenza || "",
      assegnato_a: step?.assegnato_a || ""
    });
    setStepDialog({ open: true, step, parentId });
  };

  const handleSaveStep = async () => {
    if (!stepForm.titolo.trim()) {
      handleApiError({ message: t("errors.requiredTitle") });
      return;
    }

    try {
      const payload = {
        ...stepForm,
        parent_step_id: stepDialog.parentId || null,
        data_scadenza: stepForm.data_scadenza || null,
        assegnato_a: (stepForm.assegnato_a && stepForm.assegnato_a !== "none") ? stepForm.assegnato_a : null
      };

      if (stepDialog.step) {
        await api.put(`${getApiPrefix()}/projects/${projectId}/steps/${stepDialog.step.id}`, payload);
        showSuccess(t("success.stepUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/projects/${projectId}/steps`, payload);
        showSuccess(t("success.stepCreated"));
      }
      setStepDialog({ open: false, step: null, parentId: null });
      fetchProject();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const handleToggleStepComplete = async (step) => {
    try {
      await api.put(`${getApiPrefix()}/projects/${projectId}/steps/${step.id}`, {
        completato: !step.completato
      });
      showSuccess(step.completato ? "Step riaperto" : "Step completato");
      fetchProject();
    } catch (err) {
      handleApiError(err, t("errors.updateFailed"));
    }
  };

  const handleDeleteStep = async () => {
    try {
      await api.delete(`${getApiPrefix()}/projects/${projectId}/steps/${deleteDialog.item.id}`);
      showSuccess(t("success.stepDeleted"));
      setDeleteDialog({ open: false, type: null, item: null });
      fetchProject();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  // Participants
  const handleAddParticipant = async () => {
    const ids = Array.from(selectedParticipants).filter(Boolean);
    if (ids.length === 0) {
      handleApiError({ message: t("errors.requiredSelectUser") });
      return;
    }
    try {
      const res = await api.post(`${getApiPrefix()}/projects/${projectId}/participants/bulk`, {
        user_ids: ids
      });
      const added = res?.data?.added ?? ids.length;
      const skipped = res?.data?.skipped ?? 0;
      if (added > 0 && skipped > 0) {
        showSuccess(`${added} partecipanti aggiunti, ${skipped} già presenti`);
      } else if (added > 0) {
        showSuccess(`${added} partecipant${added === 1 ? 'e aggiunto' : 'i aggiunti'}`);
      } else {
        showSuccess("Nessun nuovo partecipante da aggiungere");
      }
      setParticipantDialog(false);
      setSelectedParticipants(new Set());
      setSelectedParticipant("");
      setParticipantSearch("");
      fetchProject();
    } catch (err) {
      handleApiError(err, t("errors.errorAddParticipant"));
    }
  };

  const handleRemoveParticipant = async (participantId) => {
    try {
      await api.delete(`${getApiPrefix()}/projects/${projectId}/participants/${participantId}`);
      showSuccess(t("success.participantRemoved"));
      fetchProject();
    } catch (err) {
      handleApiError(err, t("errors.errorRemoval"));
    }
  };

  // Notes
  const handleSendNote = async () => {
    if (!noteInput.trim()) return;

    try {
      await api.post(`${getApiPrefix()}/projects/${projectId}/notes`, {
        contenuto: noteInput,
        step_id: selectedStepForNote || null
      });
      setNoteInput("");
      fetchProject();
    } catch (err) {
      handleApiError(err, t("errors.errorSendNote"));
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`${getApiPrefix()}/projects/${projectId}/notes/${noteId}`);
      showSuccess(t("success.noteDeleted"));
      fetchProject();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  // Update project status
  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`${getApiPrefix()}/projects/${projectId}`, { stato: newStatus });
      showSuccess(t("success.statusUpdated"));
      fetchProject();
    } catch (err) {
      handleApiError(err, t("errors.updateFailed"));
    }
  };

  // Step Component (recursive)
  const StepItem = ({ step, depth = 0 }) => {
    const hasChildren = step.children && step.children.length > 0;
    const isExpanded = expandedSteps[step.id];

    return (
      <div className={`${depth > 0 ? "ml-6 border-l-2 border-muted pl-4" : ""}`}>
        <div 
          className={`flex items-start gap-3 p-3 rounded-lg transition-colors hover:bg-muted/50 group ${
            step.completato ? "opacity-60" : ""
          }`}
        >
          {/* Checkbox */}
          <div className="pt-0.5">
            <Checkbox
              checked={step.completato}
              onCheckedChange={() => handleToggleStepComplete(step)}
              className="h-5 w-5"
            />
          </div>

          {/* Expand/Collapse for children */}
          {hasChildren ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 -ml-1"
              onClick={() => toggleStepExpand(step.id)}
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          ) : (
            <div className="w-6" />
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`font-medium ${step.completato ? "line-through text-muted-foreground" : ""}`}>
                {step.titolo}
              </span>
              {step.assegnato_a && (step.assegnato_nome || step.assegnato_cognome) && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <div className="h-3.5 w-3.5 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-[8px] font-medium text-primary">
                      {step.assegnato_nome?.[0]}{step.assegnato_cognome?.[0]}
                    </span>
                  </div>
                  {step.assegnato_nome}
                </Badge>
              )}
              {step.notes_count > 0 && (
                <Badge variant="secondary" className="text-xs">
                  <MessageSquare className="h-3 w-3 mr-1" /> {step.notes_count}
                </Badge>
              )}
              {step.data_scadenza && (
                <Badge 
                  variant="outline" 
                  className={`text-xs ${isOverdue(step.data_scadenza) && !step.completato ? "border-red-300 text-red-600" : ""}`}
                >
                  <Calendar className="h-3 w-3 mr-1" /> {formatDate(step.data_scadenza)}
                </Badge>
              )}
            </div>
            {step.descrizione && (
              <p className="text-sm text-muted-foreground mt-1">{step.descrizione}</p>
            )}
            {step.completato && step.completed_at && (
              <p className="text-xs text-green-600 mt-1">
                Completato il {formatDateTime(step.completed_at)}
              </p>
            )}
          </div>

          {/* Actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openStepDialog(null, step.id)}>
                <Plus className="h-4 w-4 mr-2" /> Aggiungi sotto-step
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedStepForNote(step.id)}>
                <MessageSquare className="h-4 w-4 mr-2" /> Aggiungi nota
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => openStepDialog(step)}>
                <Edit className="h-4 w-4 mr-2" />{t('common.edit')}</DropdownMenuItem>
              <DropdownMenuItem 
                className="text-red-600"
                onClick={() => setDeleteDialog({ open: true, type: "step", item: step })}
              >
                <Trash2 className="h-4 w-4 mr-2" />{t('common.delete')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="mt-1">
            {step.children.map(child => (
              <StepItem key={child.id} step={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Progetto non trovato</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Torna indietro
        </Button>
      </div>
    );
  }

  // Get available users for invite (not already participants)
  const participantIds = project.participants?.map(p => p.user_id) || [];
  const availableUsers = users.filter(u => !participantIds.includes(u.id));

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
        <div className="flex items-start gap-2 sm:gap-4 min-w-0 flex-1">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="flex-shrink-0 h-10 w-10 sm:h-9 sm:w-9">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="text-xl sm:text-2xl font-bold break-words">{project.titolo}</h1>
              {project.is_ricorrente && (
                <Badge variant="outline" className="border-purple-300 text-purple-600">
                  <Repeat className="h-3 w-3 mr-1" /> {recurrenceLabels[project.tipo_ricorrenza]}
                </Badge>
              )}
            </div>
            {project.descrizione && (
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">{project.descrizione}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto ml-11 sm:ml-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-10 sm:h-9">
                <Badge className={statusLabels[project.stato].color}>
                  {statusLabels[project.stato].label}
                </Badge>
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {Object.entries(statusLabels).map(([key, { label }]) => (
                <DropdownMenuItem key={key} onClick={() => handleStatusChange(key)}>
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 md:grid-cols-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('common.priority')}</span>
              <Badge className={priorityColors[project.priorita]}>
                {project.priorita.charAt(0).toUpperCase() + project.priorita.slice(1)}
              </Badge>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('invoices.dueDate')}</span>
              <span className={`font-medium ${isOverdue(project.data_scadenza) && project.stato !== "completato" ? "text-red-600" : ""}`}>
                {project.data_scadenza ? formatDate(project.data_scadenza) : "-"}
              </span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("common.category")}</span>
              {project.categoria_nome ? (
                <Badge variant="outline" style={{ borderColor: project.categoria_colore }}>
                  {project.categoria_nome}
                </Badge>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Avanzamento</span>
                <span className="font-medium">{project.progress || 0}%</span>
              </div>
              <Progress value={project.progress || 0} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
        {/* Steps Section */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 gap-2">
              <div className="min-w-0">
                <CardTitle className="text-base sm:text-lg">Step e Attività</CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  {project.steps?.filter(s => s.completato).length || 0} di {project.steps?.length || 0} completati
                </CardDescription>
              </div>
              <Button size="sm" onClick={() => openStepDialog()} data-testid="add-step-btn" className="flex-shrink-0 h-10 w-10 sm:h-9 sm:w-auto sm:px-3">
                <Plus className="h-5 w-5 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Aggiungi Step</span>
              </Button>
            </CardHeader>
            <CardContent>
              {stepTree.length > 0 ? (
                <div className="space-y-1">
                  {stepTree.map(step => (
                    <StepItem key={step.id} step={step} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                  <p>Nessuno step ancora</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => openStepDialog()}>
                    <Plus className="h-4 w-4 mr-2" /> Aggiungi il primo step
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Note e Discussioni
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] pr-4">
                {project.notes && project.notes.length > 0 ? (
                  <div className="space-y-4">
                    {[...project.notes].reverse().map(note => (
                      <div key={note.id} className="flex gap-3 group">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-primary">
                            {note.autore_nome?.split(" ").map(n => n[0]).join("") || "?"}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{note.autore_nome}</span>
                            <span className="text-xs text-muted-foreground">{formatDateTime(note.created_at)}</span>
                            {note.step_titolo && (
                              <Badge variant="outline" className="text-xs">
                                Re: {note.step_titolo}
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 ml-auto opacity-0 group-hover:opacity-100"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          <p className="text-sm mt-1">{note.contenuto}</p>
                        </div>
                      </div>
                    ))}
                    <div ref={notesEndRef} />
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Nessuna nota ancora. Inizia la discussione!
                  </div>
                )}
              </ScrollArea>
              
              <Separator className="my-4" />
              
              {/* Note Input */}
              <div className="space-y-2">
                {selectedStepForNote && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Rispondi a:</span>
                    <Badge variant="secondary">
                      {project.steps?.find(s => s.id === selectedStepForNote)?.titolo || "Step"}
                    </Badge>
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => setSelectedStepForNote(null)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    placeholder="Scrivi una nota..."
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendNote()}
                    data-testid="note-input"
                    className="h-10"
                  />
                  <Button onClick={handleSendNote} disabled={!noteInput.trim()} className="h-10 w-10 sm:w-auto sm:px-4 p-0 sm:p-2 flex-shrink-0">
                    <Send className="h-5 w-5 sm:h-4 sm:w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Participants */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Partecipanti ({project.participants?.length || 0})
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => setParticipantDialog(true)} className="h-10 w-10 sm:h-9 sm:w-9 p-0">
                <UserPlus className="h-5 w-5 sm:h-4 sm:w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {project.participants && project.participants.length > 0 ? (
                <div className="space-y-2">
                  {project.participants.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 group">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xs font-medium text-primary">
                            {p.nome?.[0]}{p.cognome?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">{p.nome} {p.cognome}</p>
                          <p className="text-xs text-muted-foreground">{p.ruolo === "owner" ? "Proprietario" : "Membro"}</p>
                        </div>
                      </div>
                      {p.ruolo !== "owner" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100"
                          onClick={() => handleRemoveParticipant(p.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Solo tu in questo progetto
                </p>
              )}
            </CardContent>
          </Card>

          {/* Project Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informazioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Creato da</span>
                <span>{project.creatore_nome || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Data creazione</span>
                <span>{formatDate(project.created_at)}</span>
              </div>
              {project.is_ricorrente && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-purple-600">
                      <Repeat className="h-4 w-4" />
                      <span className="font-medium">Ricorrenza</span>
                    </div>
                    <p className="text-muted-foreground">
                      Ogni {project.intervallo_ricorrenza > 1 ? `${project.intervallo_ricorrenza} ` : ""}
                      {project.tipo_ricorrenza === "giornaliero" ? "giorno/i" :
                       project.tipo_ricorrenza === "settimanale" ? "settimana/e" :
                       project.tipo_ricorrenza === "mensile" ? "mese/i" : "anno/i"}
                      {project.fine_ricorrenza && ` fino al ${formatDate(project.fine_ricorrenza)}`}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* I tuoi incarichi */}
          {currentUser && (() => {
            const mySteps = (project.steps || []).filter(s => s.assegnato_a === currentUser.id);
            if (mySteps.length === 0) return null;
            const pending = mySteps.filter(s => !s.completato);
            const done = mySteps.filter(s => s.completato);
            return (
              <Card className="border-indigo-200 bg-indigo-50/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-indigo-700">
                    <ClipboardList className="h-4 w-4" />
                    I tuoi incarichi ({mySteps.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {pending.map(s => (
                    <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg bg-white border text-sm">
                      <Circle className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0" />
                      <span className="truncate">{s.titolo}</span>
                      {s.data_scadenza && (
                        <span className={`ml-auto text-xs flex-shrink-0 ${isOverdue(s.data_scadenza) ? "text-red-500" : "text-muted-foreground"}`}>
                          {formatDate(s.data_scadenza)}
                        </span>
                      )}
                    </div>
                  ))}
                  {done.map(s => (
                    <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/50 border text-sm opacity-60">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                      <span className="truncate line-through">{s.titolo}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })()}
        </div>
      </div>

      {/* Step Dialog */}
      <Dialog open={stepDialog.open} onOpenChange={(open) => setStepDialog({ ...stepDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {stepDialog.step ? "Modifica Step" : stepDialog.parentId ? "Nuovo Sotto-step" : "Nuovo Step"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titolo *</Label>
              <Input
                value={stepForm.titolo}
                onChange={(e) => setStepForm({ ...stepForm, titolo: e.target.value })}
                placeholder="Titolo dello step"
                data-testid="step-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Textarea
                value={stepForm.descrizione}
                onChange={(e) => setStepForm({ ...stepForm, descrizione: e.target.value })}
                placeholder={t("common.descriptionPlaceholder")}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Scadenza (opzionale)</Label>
              <Input
                type="date"
                value={stepForm.data_scadenza}
                onChange={(e) => setStepForm({ ...stepForm, data_scadenza: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Assegna a</Label>
              <SearchableSelect
                options={[
                  { value: "none", label: "Nessuno" },
                  ...(project?.participants || []).map(p => ({ value: p.user_id, label: `${p.nome} ${p.cognome}` }))
                ]}
                value={stepForm.assegnato_a}
                onValueChange={(v) => setStepForm({ ...stepForm, assegnato_a: v })}
                placeholder={t("common.none")}
                searchPlaceholder="Cerca partecipante..."
                testId="select-assegnato"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStepDialog({ open: false, step: null, parentId: null })}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveStep} data-testid="save-step-btn">
              {stepDialog.step ? t("common.save") : "Crea Step"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Participant Dialog */}
      <Dialog open={participantDialog} onOpenChange={(open) => {
        setParticipantDialog(open);
        if (!open) { setSelectedParticipants(new Set()); setParticipantSearch(""); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("projects.inviteParticipant")}</DialogTitle>
            <DialogDescription>
              Seleziona uno o più utenti da aggiungere al progetto
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <Input
              placeholder="Cerca utente..."
              value={participantSearch}
              onChange={(e) => setParticipantSearch(e.target.value)}
              data-testid="participant-search"
            />
            {(() => {
              const q = participantSearch.trim().toLowerCase();
              const filtered = availableUsers.filter(u => {
                if (!q) return true;
                const label = `${u.nome} ${u.cognome} ${u.email}`.toLowerCase();
                return label.includes(q);
              });
              const filteredIds = filtered.map(u => u.id);
              const allSelected = filtered.length > 0 && filteredIds.every(id => selectedParticipants.has(id));
              const toggleAll = () => {
                setSelectedParticipants(prev => {
                  const next = new Set(prev);
                  if (allSelected) {
                    filteredIds.forEach(id => next.delete(id));
                  } else {
                    filteredIds.forEach(id => next.add(id));
                  }
                  return next;
                });
              };
              const toggleOne = (id) => {
                setSelectedParticipants(prev => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                });
              };
              return (
                <>
                  <div className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2 border">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="select-all-participants">
                      <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                      <span className="text-sm font-medium">
                        {allSelected ? "Deseleziona tutti" : `Seleziona tutti${q ? ' (filtrati)' : ''}`}
                      </span>
                    </label>
                    <span className="text-xs text-muted-foreground" data-testid="selected-count">
                      {selectedParticipants.size} selezionati
                    </span>
                  </div>
                  <ScrollArea className="h-64 border rounded-md">
                    <div className="p-2 space-y-1">
                      {filtered.length === 0 ? (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                          {availableUsers.length === 0 ? "Tutti gli utenti sono già partecipanti" : "Nessun utente trovato"}
                        </div>
                      ) : filtered.map(u => (
                        <label
                          key={u.id}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                          data-testid={`participant-option-${u.id}`}
                        >
                          <Checkbox
                            checked={selectedParticipants.has(u.id)}
                            onCheckedChange={() => toggleOne(u.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{u.nome} {u.cognome}</div>
                            <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </ScrollArea>
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setParticipantDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAddParticipant} disabled={selectedParticipants.size === 0} data-testid="btn-add-participants">
              <UserPlus className="h-4 w-4 mr-2" />
              Aggiungi{selectedParticipants.size > 1 ? ` (${selectedParticipants.size})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questo step?
              {deleteDialog.item?.children?.length > 0 && " Tutti i sotto-step verranno eliminati."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: null, item: null })}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDeleteStep}>{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
